import { array, boolean, number, object, string } from 'yup';

// Base schema for common fields
const baseSchema = {
  // Core configuration fields
  active: boolean().required('Active status is required'),
  direction: string()
    .oneOf(['buy', 'sell'], 'Direction must be either buy or sell')
    .required('Direction is required'),

  // Mint address (Solana token address)
  mint: string()
    .required('Token mint address is required')
    .test('is-valid-solana-address', 'Must be a valid Solana address', value => {
      if (!value) return false;
      // Solana addresses are base58 encoded strings, typically 32-44 characters
      // This is a basic check - a more thorough validation would decode and verify
      const solanaAddressRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
      return solanaAddressRegex.test(value);
    }),

  // Price range
  price_range: object({
    price_min: string()
      .required('Minimum price is required')
      .test(
        'is-valid-non-negative-number',
        'Must be a number greater than or equal to 0',
        value => {
          if (!value && value !== '0') return false;
          const num = parseFloat(value);
          return !isNaN(num) && num >= 0;
        },
      ),
    price_max: string()
      .required('Maximum price is required')
      .test(
        'is-valid-non-negative-number',
        'Must be a number greater than or equal to 0',
        value => {
          if (!value && value !== '0') return false;
          const num = parseFloat(value);
          return !isNaN(num) && num >= 0;
        },
      )
      .test(
        'is-greater-or-equal-to-min',
        'Max price must be greater than or equal to min price',
        function (value) {
          const { price_min } = this.parent;
          if ((!value && value !== '0') || (!price_min && price_min !== '0')) return true;
          return parseFloat(value) >= parseFloat(price_min);
        },
      ),
  }),

  // Sleep duration range
  sleep_duration_range: object({
    sleep_duration_min: number()
      .required('Minimum sleep duration is required')
      .min(0, 'Sleep duration must be at least 0')
      .integer('Must be an integer'),
    sleep_duration_max: number()
      .required('Maximum sleep duration is required')
      .min(0, 'Sleep duration must be at least 0')
      .integer('Must be an integer')
      .test(
        'is-greater-than-min',
        'Max sleep duration must be greater than or equal to min',
        function (value) {
          const { sleep_duration_min } = this.parent;
          if (value === undefined || sleep_duration_min === undefined) return true;
          return value >= sleep_duration_min;
        },
      ),
  }),

  // Slippage
  slippage: string()
    .required('Slippage percentage is required')
    .test('is-valid-percentage', 'Must be a valid percentage between 0 and 100', value => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num > 0 && num <= 100;
    }),

  // Total volume
  total_volume: string()
    .required('Total volume is required')
    .test('is-valid-positive-number', 'Must be a number greater than 0', value => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num > 0;
    }),

  // Trade size range
  trade_size_range: object({
    trade_size_min: string()
      .required('Minimum trade size is required')
      .test('is-valid-positive-number', 'Must be a number greater than 0', value => {
        if (!value) return false;
        const num = parseFloat(value);
        return !isNaN(num) && num > 0;
      }),
    trade_size_max: string()
      .required('Maximum trade size is required')
      .test('is-valid-positive-number', 'Must be a number greater than 0', value => {
        if (!value) return false;
        const num = parseFloat(value);
        return !isNaN(num) && num > 0;
      })
      .test(
        'is-greater-than-min',
        'Max trade size must be greater than min trade size',
        function (value) {
          const { trade_size_min } = this.parent;
          if (!value || !trade_size_min) return true;
          return parseFloat(value) > parseFloat(trade_size_min);
        },
      ),
  }),
};

// Schema for create mode (includes key_pairs)
export const createModeSchema = object({
  ...baseSchema,
  // Wallet IDs (managed through creating a new config)
  wallet_ids: array()
    .of(number().required('Wallet ID is required'))
    .required('At least one wallet must be selected')
    .min(1, 'At least one wallet must be selected'),
});

// Schema for edit mode (key_pairs are optional)
export const editModeSchema = object({
  ...baseSchema,
  // Wallet IDs (wallets already linked to the system)
  wallet_ids: array().of(number().required('Wallet ID is required')),
});
